import { useState, useEffect, useMemo } from 'react'
import {
    FaCloudUploadAlt,
    FaCheckCircle,
    FaInfoCircle,
    FaPlayCircle,
    FaTimesCircle,
    FaClock,
    FaFilePdf,
} from 'react-icons/fa'
import axiosInstance from '@/api/axiosInstance'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import './Steps.css'

interface CertificateStepProps {
    onComplete?: () => void
    isVerified?: boolean
}

const LABOR_CERTIFICATE_NAME = 'Certificado Único Laboral (CUL)'
const LABOR_CERTIFICATE_VIDEO = 'https://www.youtube.com/watch?v=ZGGIfD5es7E'

const CertificateStep = ({ onComplete, isVerified = false }: CertificateStepProps) => {
    const [file, setFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)
    const [certificateStatus, setCertificateStatus] = useState<any>(null)
    const [statusLoading, setStatusLoading] = useState(true)
    
    // Fetch certificate status on mount
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await axiosInstance.get('/verification-documents/labor_certificate_status/')
                setCertificateStatus(response.data)
            } catch (error) {
                console.log('No certificate status found or error:', error)
            } finally {
                setStatusLoading(false)
            }
        }
        fetchStatus()
    }, [])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0]
        if (selectedFile) {
            if (selectedFile.size > 10 * 1024 * 1024) {
                alert('El archivo no debe superar los 10 MB')
                return
            }

            if (selectedFile.type !== 'application/pdf' && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
                alert('Solo se permiten archivos PDF')
                return
            }

            setFile(selectedFile)
        }
    }
    
    const handleWatchVideo = () => {
        window.open(LABOR_CERTIFICATE_VIDEO, '_blank')
    }

    const currentStatus = useMemo(() => {
        if (isVerified) return 'approved'
        return certificateStatus?.status || 'not_uploaded'
    }, [certificateStatus?.status, isVerified])

    const statusDetails = useMemo(() => {
        switch (currentStatus) {
            case 'approved':
                return {
                    label: 'Aprobado',
                    description: 'Tu certificado ha sido aprobado. Ya puedes publicar tus servicios.',
                    icon: FaCheckCircle,
                    tone: 'certificate-status--approved',
                }
            case 'pending':
                return {
                    label: 'En revisión',
                    description: 'Estamos revisando tu documento. Te notificaremos cuando haya una respuesta.',
                    icon: FaClock,
                    tone: 'certificate-status--pending',
                }
            case 'rejected':
                return {
                    label: 'Rechazado',
                    description: 'Revisa el motivo indicado y vuelve a subir tu certificado en PDF.',
                    icon: FaTimesCircle,
                    tone: 'certificate-status--rejected',
                }
            default:
                return {
                    label: 'Sin subir',
                    description: 'Sube tu Certificado Único Laboral en formato PDF para verificar tu cuenta.',
                    icon: FaCloudUploadAlt,
                    tone: 'certificate-status--default',
                }
        }
    }, [currentStatus])

    const rejectionReason = certificateStatus?.rejection_reason
    const StatusIcon = statusDetails.icon

    const handleUpload = async () => {
        if (currentStatus === 'approved' && !file) {
            onComplete?.()
            return
        }

        if (!file) {
            alert('Debes seleccionar un archivo PDF para continuar.')
            return
        }

        setUploading(true)
        try {
            console.log('🚀 Starting certificate upload (direct method)...')
            
            // Use direct backend upload instead of signed URL
            const formData = new FormData()
            formData.append('file', file)
            formData.append('folder', 'verification_documents')
            formData.append('document_type', 'labor_certificate')
            formData.append('document_name', file.name)

            console.log('📤 Uploading file directly through backend...')
            const uploadResponse = await axiosInstance.post('/verification-documents/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })

            console.log('✅ Upload response:', uploadResponse.data)
            const file_url = uploadResponse.data.document_url

            console.log('✅ Upload complete! Testing URL access...')
            
            // Test if the URL is accessible
            try {
                const testResponse = await fetch(file_url, { method: 'HEAD' })
                if (testResponse.ok) {
                    console.log('✅ File URL is publicly accessible:', file_url)
                } else {
                    console.warn('⚠️ File URL returned status:', testResponse.status, file_url)
                }
            } catch (testError) {
                console.warn('⚠️ Could not verify file URL accessibility:', testError)
            }

            alert('Tu Certificado Único Laboral se subió correctamente y está pendiente de revisión. ¡Gracias!')

            const response = await axiosInstance.get('/verification-documents/labor_certificate_status/')
            setCertificateStatus(response.data)
            setFile(null)

            onComplete?.()
        } catch (error: any) {
            console.error('❌ Upload error:', error)
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            })
            alert(`Error al subir el documento: ${error.response?.data?.error || error.message || 'Error desconocido'}`)
        } finally {
            setUploading(false)
        }
    }
 
    if (statusLoading) {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 0' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Verificando estado del certificado...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="step-container certificate-step">
            <div className="certificate-header">
                <h3 className="certificate-title">{LABOR_CERTIFICATE_NAME}</h3>
                <p className="certificate-subtitle">Sube tu {LABOR_CERTIFICATE_NAME} para verificar tu identidad</p>
            </div>

            <div className="certificate-content">
                <section className="certificate-info-card">
                    <div className="certificate-info-header">
                        <FaInfoCircle />
                        <h4>¿Por qué lo necesitamos?</h4>
                    </div>
                    <p className="certificate-info-text">
                        Es un documento oficial que valida tu identidad, experiencia laboral y antecedentes. Es obligatorio para que los clientes confíen en tus servicios.
                    </p>

                    <div className={`certificate-status ${statusDetails.tone}`}>
                        <span className="certificate-status-icon">
                            <StatusIcon />
                        </span>
                        <div className="certificate-status-text">
                            <strong>{statusDetails.label}</strong>
                            <span>{statusDetails.description}</span>
                            {rejectionReason && (
                                <em>Motivo: {rejectionReason}</em>
                            )}
                        </div>
                    </div>

                    <button type="button" className="certificate-video-button" onClick={handleWatchVideo}>
                        <FaPlayCircle /> Cómo obtener tu CUL en 5 minutos
                    </button>
                </section>

                <section className="certificate-upload-card">
                    <h4>Subir documento (PDF)</h4>

                    {file ? (
                        <div className="certificate-file-chip">
                            <FaFilePdf className="certificate-file-icon" />
                            <div className="certificate-file-details">
                                <span className="certificate-file-name">{file.name}</span>
                                <span className="certificate-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                            </div>
                            <label htmlFor="certificate-upload" className="certificate-file-change">
                                Cambiar
                            </label>
                        </div>
                    ) : (
                        <label htmlFor="certificate-upload" className="certificate-dropzone">
                            <FaCloudUploadAlt />
                            <span className="certificate-dropzone-title">Seleccionar archivo PDF</span>
                            <span className="certificate-dropzone-hint">Hasta 10 MB • Debe ser el CUL</span>
                        </label>
                    )}

                    <input
                        type="file"
                        id="certificate-upload"
                        accept="application/pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={uploading}
                    />
                </section>
            </div>

            <div className="step-actions">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={handleUpload}
                    disabled={uploading || (currentStatus !== 'approved' && !file)}
                >
                    {uploading
                        ? 'Subiendo...'
                        : currentStatus === 'approved'
                            ? 'Continuar'
                            : currentStatus === 'pending'
                                ? 'Actualizar documento'
                                : 'Subir certificado'}
                </Button>
            </div>
        </div>
    )
}

export default CertificateStep
